import React from 'react';

import Bone from '@lightspeed/cirrus-bone';
import { Button } from '@lightspeed/flame/Button';
import Card, { CardSection } from '@lightspeed/cirrus-card';
import { Divider } from '@lightspeed/flame/Divider';
import { spacing } from '@lightspeed/cirrus-tokens/utils';
import Text, { TextLink } from '@lightspeed/cirrus-text';

import { breakTextIntoLines } from 'pages/utils/text';

import {
  VendorDetails,
  AccountContainer,
  AddressContainer,
  NotesContainer,
  breakLine,
  breakWord,
} from './VendorReturnVendorDetails.style';

const VendorReturnVendorDetails = ({
  vendor: {
    vendorID,
    name,
    contactFirstName,
    contactLastName,
    accountNumber,
    Contact: { data: contactData } = {},
    Note: { data: noteData } = {},
  } = {},
  isHiding,
  isLoading,
  isVendorDetailsHidden,
  isVendorEditEnabled,
  onEditVendorClick,
  onVendorDetailsChange,
}) => {
  const { email, email2, phoneWork, mobile, address1, address2, city, state, country, zip } =
    contactData || {};
  const { note } = noteData || {};

  const isAccountEmpty =
    !contactFirstName && !contactLastName && !accountNumber && !email && !email2 && !mobile;
  const isAddressEmpty = !address1 && !address2 && !city && !state && !country && !zip;
  const isVendorReturnsEmpty = isAccountEmpty && isAddressEmpty && !note;

  if (!vendorID && !isLoading) {
    return null;
  }

  return !isLoading ? (
    <Card className="cr-mb-2">
      <CardSection className="cr-flex cr-items-center" noSpacing>
        <Text body bold className="cr-flex-1 cr-ph-2 cr-pv-1 notranslate" css={breakWord}>
          {name}
        </Text>
        <div className="cr-mv-1 cr-mh-2">
          <Button
            size="small"
            onClick={onEditVendorClick}
            disabled={!isVendorEditEnabled}
            data-test="vendor-return-vendor-edit-details-button"
          >
            Edit details
          </Button>
        </div>
        <div className="cr-mv-1 cr-mr-2">
          <Button
            size="small"
            onClick={!isHiding ? onVendorDetailsChange : null}
            disabled={!isVendorEditEnabled}
            loading={isHiding}
            data-test={`vendor-return-${
              isVendorDetailsHidden ? 'show' : 'hide'
            }-vendor-details-link`}
          >
            {!isVendorDetailsHidden ? 'Hide details' : 'Show details'}
          </Button>
        </div>
      </CardSection>
      {!isVendorDetailsHidden && <Divider />}
      {!isVendorReturnsEmpty && !isVendorDetailsHidden && (
        <VendorDetails>
          {!isAccountEmpty && (
            <AccountContainer body>
              {(!!contactFirstName || !!contactLastName) && (
                <span data-test="vendor-return-vendor-contact" className="notranslate">
                  {`${contactFirstName} ${contactLastName}`}
                  <br />
                </span>
              )}
              {!!accountNumber && (
                <span data-test="vendor-return-vendor-account">
                  Account: <var>{accountNumber}</var>
                  <br />
                </span>
              )}
              {!!email && (
                <span data-test="vendor-return-vendor-email" className="notranslate">
                  <TextLink href={`mailto: ${email}`} target="_blank">
                    {email}
                  </TextLink>
                  <br />
                </span>
              )}
              {!!email2 && (
                <span data-test="vendor-return-vendor-email2" className="notranslate">
                  <TextLink href={`mailto: ${email2}`} target="_blank">
                    {email2}
                  </TextLink>
                  <br />
                </span>
              )}
              {!!phoneWork && (
                <span data-test="vendor-return-vendor-phone" className="notranslate">
                  {phoneWork}
                  <br />
                </span>
              )}
              {!!mobile && (
                <span data-test="vendor-return-vendor-mobile" className="notranslate">
                  {mobile}
                  <br />
                </span>
              )}
            </AccountContainer>
          )}
          {!isAddressEmpty && (
            <AddressContainer body className="notranslate">
              {!!address1 && (
                <span data-test="vendor-return-vendor-address1">
                  {address1}
                  <br />
                </span>
              )}
              {!!address2 && (
                <span data-test="vendor-return-vendor-address2">
                  {address2}
                  <br />
                </span>
              )}
              {(!!city || !!state) && (
                <span data-test="vendor-return-vendor-city-state">
                  {`${city}${!!city && !!state && ','} ${state}`}
                  <br />
                </span>
              )}
              <span data-test="vendor-return-vendor-zip-country">{`${zip} ${country}`}</span>
            </AddressContainer>
          )}
          {!!note && (
            <NotesContainer
              body
              element="div"
              data-test="vendor-return-vendor-note"
              className="notranslate"
            >
              {breakTextIntoLines({ text: note, lineBreak: '\n', lineCSS: breakLine })}
            </NotesContainer>
          )}
        </VendorDetails>
      )}
      {isVendorReturnsEmpty && !isVendorDetailsHidden && (
        <CardSection className="cr-text-center">
          <Text bodySmall className="cr-pv-1" data-test="vendor-return-vendor-no-vendor">
            No vendor details to display
          </Text>
        </CardSection>
      )}
    </Card>
  ) : (
    <VendorReturnVendorDetailsSkeleton />
  );
};

export const VendorReturnVendorDetailsSkeleton = () => (
  <Card className="cr-mb-2">
    <CardSection className="cr-flex cr-items-center">
      <Text body bold className="cr-flex-1">
        <Bone height={spacing(4)} width="10rem" />
      </Text>
      <Bone height={spacing(4)} width="8rem" />
    </CardSection>
    <Divider />
    <VendorDetails>
      <AccountContainer body>
        <Bone className="cr-mb-2" width="100%" />
        <Bone className="cr-mb-2" width="90%" />
        <Bone className="cr-mb-2" width="80%" />
        <Bone width="70%" />
      </AccountContainer>
      <AddressContainer body>
        <Bone className="cr-mb-2" width="100%" />
        <Bone className="cr-mb-2" width="90%" />
        <Bone className="cr-mb-2" width="80%" />
        <Bone width="70%" />
      </AddressContainer>
      <NotesContainer body>
        <span>
          <Bone className="cr-mb-2" width="100%" />
          <Bone className="cr-mb-2" width="90%" />
          <Bone className="cr-mb-2" width="80%" />
          <Bone width="70%" />
        </span>
      </NotesContainer>
    </VendorDetails>
  </Card>
);

export default VendorReturnVendorDetails;
